/**
 * 智慧客服頁(REQ-20260518001):LINE 對話列表 + 對話流 + 文字回覆。
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../features/auth/store';
import { ContactList } from '../features/line/ContactList';
import { MessageStream } from '../features/line/MessageStream';
import { ComposerBox } from '../features/line/ComposerBox';
import { BindCustomerModal } from '../features/line/BindCustomerModal';
import { RichMenuToolModal } from '../features/line/RichMenuToolModal';
import { AssistantSettingsModal } from '../features/line/AssistantSettingsModal';
import {
  useCloseContact,
  useLineContactDetail,
  useMarkRead,
  useReleaseContact,
  useTakeoverContact,
  useUnbindCustomer,
} from '../features/line/lineApi';

export function SmartServicePage(): JSX.Element {
  const me = useAuthStore((s) => s.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [bindOpen, setBindOpen] = useState(false);
  const [richMenuOpen, setRichMenuOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);

  const { data: detail } = useLineContactDetail(selectedId);
  const takeover = useTakeoverContact();
  const release = useReleaseContact();
  const close = useCloseContact();
  const markRead = useMarkRead();
  const unbind = useUnbindCustomer();

  // REQ-20260518002:?contactId=N 深連結(從客戶頁「前往對話」進來,也支援已掛載時再次切換)
  const contactIdParam = searchParams.get('contactId');
  useEffect(() => {
    if (!contactIdParam) return;
    const n = Number(contactIdParam);
    if (Number.isFinite(n) && n > 0) {
      setSelectedId(n);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('contactId');
    setSearchParams(next, { replace: true });
    // 刻意省略部分 deps(只在指定值變動時重跑)。
    // 註:專案未安裝 react-hooks plugin,原本這裡的 disable 指令引用了不存在的
    // 規則,反而被 ESLint 判為錯誤,故移除。導入該 plugin 時要記得補回來。
  }, [contactIdParam]);

  // 切換對話 → 把未讀清空(後端 update 是 idempotent,不必先判斷有沒有未讀)
  useEffect(() => {
    if (selectedId != null) {
      markRead.mutate(selectedId);
    }
    // 刻意省略部分 deps(只在指定值變動時重跑)。
    // 註:專案未安裝 react-hooks plugin,原本這裡的 disable 指令引用了不存在的
    // 規則,反而被 ESLint 判為錯誤,故移除。導入該 plugin 時要記得補回來。
  }, [selectedId]);

  const view = detail
    ? {
        id: detail.id,
        displayName: detail.displayName,
        pictureUrl: detail.pictureUrl,
        status: detail.status,
        assignedToId: detail.assignedToId,
        assignedToName: detail.assignedToName,
        customerId: detail.customerId,
        customerSimpleName: detail.customerSimpleName,
        customerCCode: detail.customerCCode,
      }
    : null;

  const isMine = view && view.assignedToId === me?.id;
  const isAdmin = me?.role === 'ADMIN';
  const canTakeover = view && view.status !== 'HUMAN';
  const canRelease = view && view.status === 'HUMAN' && (isMine || isAdmin);
  const canClose = view && view.status !== 'CLOSED' && (isMine || isAdmin || view.status !== 'HUMAN');
  const canSend = view && view.status === 'HUMAN' && (isMine || isAdmin);
  const disabledReason =
    !view
      ? '請先選擇對話'
      : view.status === 'CLOSED'
        ? '對話已關閉'
        : view.status === 'AI'
          ? 'AI 自動回覆中,如要人工回覆請先「接管」'
          : !isMine && !isAdmin
            ? `已被 ${view.assignedToName ?? '其他客服'} 接管`
            : undefined;

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 8rem)' }}>
      <div className="flex items-center justify-end gap-2 px-2 py-1.5 bg-white border rounded-t border-b-0">
        <button
          type="button"
          onClick={() => setRichMenuOpen(true)}
          className="text-xs border border-blue-600 text-blue-700 px-3 py-1 rounded hover:bg-blue-50"
          title="用 OpenAI 生 LINE Rich Menu(主選單)圖片"
        >
          🎨 Rich Menu 生圖
        </button>
        <button
          type="button"
          onClick={() => setAssistantOpen(true)}
          className="text-xs border border-emerald-600 text-emerald-700 px-3 py-1 rounded hover:bg-emerald-50"
          title="編輯 AI System Prompt 與服務項目資料(AI 回覆時會帶入)"
        >
          🤖 助理設定
        </button>
      </div>
    <section
      className="grid grid-cols-[18rem_1fr] gap-0 bg-white border rounded-b overflow-hidden flex-1 min-h-0"
    >
      <ContactList
        selectedId={selectedId}
        onSelect={(c) => setSelectedId(c.id)}
      />

      <div className="flex flex-col min-h-0">
        {view ? (
          <header className="border-b px-3 py-2 flex items-center gap-2 bg-white">
            {view.pictureUrl ? (
              <img
                src={view.pictureUrl}
                alt=""
                className="w-8 h-8 rounded-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-slate-200" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate flex items-center gap-2">
                <span>{view.displayName ?? '(未取得名稱)'}</span>
                {view.customerSimpleName ? (
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700"
                    title={`已綁定客戶 #${view.customerCCode ?? view.customerId}`}
                  >
                    🔗 {view.customerSimpleName}
                  </span>
                ) : null}
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-2">
                <span>狀態:{view.status}</span>
                {view.status === 'HUMAN' && view.assignedToName ? (
                  <span>・ 承辦:{view.assignedToName}</span>
                ) : null}
                {view.customerId == null ? (
                  <button
                    type="button"
                    onClick={() => setBindOpen(true)}
                    className="text-blue-600 hover:underline"
                  >
                    [綁定客戶]
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setBindOpen(true)}
                      className="text-blue-600 hover:underline"
                    >
                      [換綁]
                    </button>
                    <button
                      type="button"
                      disabled={unbind.isPending}
                      onClick={() => {
                        if (window.confirm(`確定解除「${view.customerSimpleName}」的綁定?`)) {
                          unbind.mutate(view.id);
                        }
                      }}
                      className="text-slate-500 hover:underline"
                    >
                      {unbind.isPending ? '[解除中…]' : '[解除]'}
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {canTakeover ? (
                <button
                  type="button"
                  disabled={takeover.isPending}
                  onClick={() => takeover.mutate(view.id)}
                  className="text-xs border border-blue-600 text-blue-600 px-2 py-1 rounded hover:bg-blue-50"
                >
                  {takeover.isPending ? '接管中…' : '接管'}
                </button>
              ) : null}
              {canRelease ? (
                <button
                  type="button"
                  disabled={release.isPending}
                  onClick={() => release.mutate(view.id)}
                  className="text-xs border border-emerald-600 text-emerald-700 px-2 py-1 rounded hover:bg-emerald-50"
                >
                  {release.isPending ? '釋放中…' : '釋放(回 AI)'}
                </button>
              ) : null}
              {canClose ? (
                <button
                  type="button"
                  disabled={close.isPending}
                  onClick={() => {
                    if (window.confirm('確定關閉這個對話?(資料保留,客戶再發訊息會自動重啟)')) {
                      close.mutate(view.id);
                    }
                  }}
                  className="text-xs border border-slate-400 text-slate-700 px-2 py-1 rounded hover:bg-slate-100"
                >
                  {close.isPending ? '關閉中…' : '關閉對話'}
                </button>
              ) : null}
            </div>
          </header>
        ) : (
          <header className="border-b px-3 py-2 text-sm text-slate-500 bg-white">
            從左側選擇一個對話
          </header>
        )}

        <MessageStream messages={detail?.messages ?? []} />

        {view ? (
          <ComposerBox
            contactId={view.id}
            disabled={!canSend}
            disabledReason={disabledReason}
            canAttach={view.customerId != null}
          />
        ) : null}
      </div>

      {view ? (
        <BindCustomerModal
          open={bindOpen}
          contactId={view.id}
          onClose={() => setBindOpen(false)}
        />
      ) : null}
    </section>

      <RichMenuToolModal open={richMenuOpen} onClose={() => setRichMenuOpen(false)} />
      <AssistantSettingsModal open={assistantOpen} onClose={() => setAssistantOpen(false)} />
    </div>
  );
}
